import { useState, useEffect } from 'react';
import { PanelLeftClose, Search, Sparkles, Loader2, Zap, Store } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSuggestedArches } from '../hooks/useSuggestedArches';
import { useSearchResults } from '../context/SearchResultsContext';
import { ArcheSidebarItem } from './ArcheSidebarItem';
import { CurrentArcheItem } from './CurrentArcheItem';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { api, MarketplaceArche } from '@/lib/api';

/** Maximum number of search results to show in sidebar */
const MAX_SIDEBAR_RESULTS = 7;

interface ArcheSidebarProps {
  currentArcheId: string;
  onToggle?: () => void;
}

export function ArcheSidebar({ currentArcheId, onToggle }: ArcheSidebarProps) {
  const { arches: suggestedArches, loading: suggestedLoading, error: suggestedError } = useSuggestedArches(currentArcheId);
  const { searchResults, searchQuery, isSearching, clearSearchResults } = useSearchResults();
  const [currentArche, setCurrentArche] = useState<MarketplaceArche | null>(null);
  const [currentArcheLoading, setCurrentArcheLoading] = useState(true);

  // Fetch current arche details
  useEffect(() => {
    if (!currentArcheId) {
      setCurrentArche(null);
      setCurrentArcheLoading(false);
      return;
    }

    setCurrentArcheLoading(true);
    api.getMarketplaceArche(currentArcheId)
      .then((arche) => setCurrentArche(arche))
      .catch(() => setCurrentArche(null))
      .finally(() => setCurrentArcheLoading(false));
  }, [currentArcheId]);

  const hasSearchResults = searchResults.length > 0 || searchQuery !== null;
  const isShowingSearchResults = hasSearchResults;

  // Limit search results to MAX_SIDEBAR_RESULTS for sidebar display
  const sidebarSearchResults = searchResults.slice(0, MAX_SIDEBAR_RESULTS);
  const hasMoreResults = searchResults.length > MAX_SIDEBAR_RESULTS;

  const arches = isShowingSearchResults ? sidebarSearchResults : suggestedArches;
  const loading = isShowingSearchResults ? isSearching : suggestedLoading;
  const error = isShowingSearchResults ? null : suggestedError;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex-1 overflow-y-auto py-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="px-3 py-2 mx-2">
              <Skeleton className="h-4 w-3/4 mb-1" />
              <Skeleton className="h-3 w-full" />
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-sm text-muted-foreground text-center">
            Unable to load suggestions
          </p>
        </div>
      );
    }

    if (arches.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-sm text-muted-foreground text-center">
            {isShowingSearchResults ? 'No matching arches found' : 'No suggestions available'}
          </p>
        </div>
      );
    }

    return (
      <div className="flex-1 overflow-y-auto py-2 flex flex-col">
        <div className="flex-1">
          {arches.map((arche) => (
            <ArcheSidebarItem
              key={arche.archId}
              arche={arche}
              isActive={arche.archId === currentArcheId}
            />
          ))}
        </div>
        {/* Marketplace button when showing search results */}
        {isShowingSearchResults && searchQuery && (
          <div className="p-3 border-t border-border mt-auto">
            <Button
              asChild
              variant="outline"
              size="sm"
              className="w-full gap-2"
            >
              <Link to={`/marketplace/browse?q=${encodeURIComponent(searchQuery)}`}>
                <Store className="h-4 w-4" />
                Search Arche Store
                {hasMoreResults && (
                  <span className="text-xs text-muted-foreground">
                    (+{searchResults.length - MAX_SIDEBAR_RESULTS})
                  </span>
                )}
              </Link>
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Current Arche Section - height matches page header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2 h-11">
          <Zap className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium text-foreground">Current Arche</span>
        </div>
        {onToggle && (
          <Button
            variant="outline"
            size="icon"
            onClick={onToggle}
            className="h-11 w-11 bg-gray-100 dark:bg-zinc-700"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="h-5 w-5" />
          </Button>
        )}
      </div>
      {currentArcheLoading ? (
        <div className="px-3 py-3 mx-2">
          <Skeleton className="h-4 w-3/4 mb-1" />
          <Skeleton className="h-3 w-full" />
        </div>
      ) : currentArche ? (
        <CurrentArcheItem arche={currentArche} />
      ) : null}

      {/* Search Results or Suggested Arches Section */}
      <div className="flex items-center justify-between p-3 border-b border-border">
        <div className="flex items-center gap-2">
          {isShowingSearchResults ? (
            <>
              <Search className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Search Results</span>
              {isSearching && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Suggested Arches</span>
            </>
          )}
        </div>
        {isShowingSearchResults && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSearchResults}
            className="h-6 px-2 text-xs"
          >
            Clear
          </Button>
        )}
      </div>
      {isShowingSearchResults && searchQuery && (
        <div className="px-3 py-2 border-b border-border bg-muted/30">
          <p className="text-xs text-muted-foreground truncate">
            &quot;{searchQuery}&quot;
          </p>
        </div>
      )}
      {renderContent()}
    </>
  );
}

export default ArcheSidebar;
